import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useCart } from './CartContext'
import productMap from './productMap'
import { formatZAR } from './utils'
import CartItem from './CartItem'

const Cart = () => {
  const { cartItems, cartCount, updateQuantity, removeFromCart, clearCart } = useCart()
  const [showConfirm, setShowConfirm] = useState(false)

  const entries = useMemo(
    () => Object.entries(cartItems).filter(([, qty]) => qty > 0),
    [cartItems],
  )

  const total = useMemo(
    () =>
      entries.reduce((sum, [id, qty]) => {
        const p = productMap[id]
        return sum + (p?.price ?? 0) * qty
      }, 0),
    [entries],
  )

  return (
    <div className="min-h-screen bg-[#faf9fe] text-gray-900">
      <section className="border-b border-gray-200 bg-gradient-to-br from-violet-700 via-violet-800 to-indigo-900 px-4 py-14 text-white sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <p className="text-sm font-semibold uppercase tracking-widest text-violet-200">
            ArrAndArr
          </p>
          <h1 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">
            Shopping Cart
          </h1>
          {cartCount > 0 && (
            <p className="mt-2 text-violet-200">
              {cartCount} item{cartCount !== 1 ? 's' : ''} in your cart
            </p>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        {entries.length === 0 ? (
          <div className="rounded-xl border border-gray-200 bg-white p-12 text-center shadow-sm">
            <p className="text-lg font-medium text-gray-500">Your cart is empty</p>
            <Link
              to="/"
              className="mt-4 inline-block rounded-lg bg-violet-800 px-6 py-3 text-sm font-semibold text-white hover:bg-violet-500"
            >
              Continue Shopping
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-6 flex items-center justify-between">
              <p className="text-sm text-gray-500">{entries.length} product{entries.length !== 1 ? 's' : ''}</p>
              <button
                onClick={() => setShowConfirm(true)}
                className="text-sm font-medium text-red-600 hover:text-red-700"
              >
                Clear cart
              </button>
            </div>

            <div className="space-y-4">
              {entries.map(([id, qty]) => (
                <CartItem
                  key={id}
                  id={id}
                  quantity={qty}
                  onUpdate={updateQuantity}
                  onRemove={removeFromCart}
                />
              ))}
            </div>

            <div className="mt-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-lg font-semibold text-gray-900">Total</span>
                <span className="text-2xl font-bold text-violet-800">
                  {formatZAR(total)}
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-500">All prices include VAT.</p>
              {total < 500 && (
                <p className="mt-1 text-xs text-emerald-600 font-medium">
                  Spend {formatZAR(500 - total)} more for free shipping!
                </p>
              )}
              {total >= 500 && (
                <p className="mt-1 text-xs text-emerald-600 font-medium">
                  Your order qualifies for free shipping.
                </p>
              )}
              <Link
                to="/Checkout"
                className="mt-6 inline-block w-full rounded-lg bg-emerald-600 px-6 py-3 text-center text-base font-semibold text-white hover:bg-emerald-700 sm:w-auto"
              >
                Proceed to Checkout
              </Link>
            </div>
          </>
        )}
      </section>

      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="mx-4 w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
            <p className="text-lg font-semibold text-gray-900">Clear cart?</p>
            <p className="mt-2 text-sm text-gray-500">
              This will remove {cartCount} item{cartCount !== 1 ? 's' : ''} from your cart.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => { clearCart(); setShowConfirm(false) }}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
              >
                Yes, clear
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Cart
